import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Award, TrendingUp, Target, CheckCircle2 } from 'lucide-react';

const LeadershipModelPage = () => {
  const levels = [
    { title: "Associate", desc: "New advisors learning the business and building first clients" },
    { title: "Marketing Director", desc: "Experienced advisors with established client base" },
    { title: "Senior Marketing Director", desc: "Top producers with leadership potential" },
    { title: "Regional Vice President", desc: "Team builders managing growing organizations" },
    { title: "Executive Vice President", desc: "Senior leaders with large, multi-state teams" }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Override Income",
      description: "Earn additional commissions from your team's production, creating leverage on your time"
    },
    {
      icon: Award,
      title: "Recognition & Rewards",
      description: "Special recognition, awards, and incentive trips for top leaders"
    },
    {
      icon: Users,
      title: "Lasting Impact",
      description: "Build an organization that creates opportunities for others"
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="leadership-model-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Leadership & Agency Builder Model
            </h1>
            <p className="text-slate-300 text-xl">
              For advisors who want to build more than a practice — build an organization that creates opportunities for others.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Leadership Advancement Path</h2>
          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-800 -translate-y-1/2"></div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {levels.map((level, idx) => (
                <div key={idx} className="relative bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#C5A059] hidden lg:flex items-center justify-center">
                    <span className="text-[#020617] text-xs font-bold">{idx + 1}</span>
                  </div>
                  <h3 className="text-white font-semibold text-center mb-2">{level.title}</h3>
                  <p className="text-slate-400 text-sm text-center">{level.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Benefits of Building a Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold text-xl mb-3">{benefit.title}</h3>
                  <p className="text-slate-400">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">How Team Building Works</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">1. Identify Potential Advisors</h3>
                  <p className="text-slate-400 text-sm">Look for motivated individuals in your network who could benefit from this career opportunity.</p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">2. Mentor & Train</h3>
                  <p className="text-slate-400 text-sm">Guide new advisors through licensing, training, and their first client appointments.</p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">3. Earn Overrides</h3>
                  <p className="text-slate-400 text-sm">As your team members place business, you earn override commissions on their production.</p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">4. Advance Your Level</h3>
                  <p className="text-slate-400 text-sm">As your organization grows, you advance to higher leadership levels with greater benefits.</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8">
              <Target className="w-12 h-12 text-[#C5A059] mb-6" />
              <h3 className="text-white font-semibold text-xl mb-4">Not Just Personal Sales</h3>
              <p className="text-slate-400 text-sm mb-6">
                The leadership path allows you to build a business that continues to grow even when you're not personally selling. It's about creating leverage through the success of others you've helped develop.
              </p>
              <ul className="space-y-2">
                {[
                  "Passive income potential through team overrides",
                  "Build an asset you can pass to your family",
                  "Create opportunities for others to succeed",
                  "Legacy that outlasts your personal production"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A059] mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Ready to Lead?</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Start your journey and build an organization that makes a lasting impact.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LeadershipModelPage;
